type AnalyticsValue = string | number | boolean | string[]
type AnalyticsParameters = Record<string, AnalyticsValue>
type DataLayerItem = Record<string, AnalyticsValue> | IArguments
type YandexMetricaFunction = ((...args: unknown[]) => void) & {
  a?: IArguments[]
  l?: number
}

declare global {
  interface Window {
    dataLayer?: DataLayerItem[]
    ym?: YandexMetricaFunction
  }
}

const gtmId = import.meta.env.VITE_GTM_ID?.trim() || 'GTM-NX9CQ486'
const ga4MeasurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim() || 'G-HSZRS8P5DF'
const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID?.trim() || 'AW-18338716662'
const yandexMetricaId = Number(import.meta.env.VITE_YANDEX_METRIKA_ID?.trim() || '110944981')
const waitlistConversionDestination = import.meta.env.VITE_GOOGLE_ADS_WAITLIST_DESTINATION?.trim()
  || 'AW-18338716662/eUNNCPiFs9QcEPa3yqhE'
const yandexGoalEvents = new Set([
  'sidewisp_agent_selected',
  'sidewisp_email_input_started',
  'sidewisp_waitlist_joined',
])
const onceOnlyEvents = new Set<string>()
let analyticsScheduled = false
let analyticsInjected = false
let yandexMetricaInitialized = false
let lastTrackedUrl = typeof window === 'undefined' ? '' : window.location.href
const activationEvents = ['pointerdown', 'keydown', 'focusin', 'scroll'] as const

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

function injectGtm() {
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
  document.head.appendChild(script)
}

function ensureYandexMetrica() {
  if (window.ym) return window.ym
  const queue = function (..._args: unknown[]) {
    queue.a = queue.a || []
    queue.a.push(arguments)
  } as YandexMetricaFunction
  queue.l = Date.now()
  window.ym = queue
  return queue
}

function initializeYandexMetrica() {
  if (!Number.isInteger(yandexMetricaId) || yandexMetricaId <= 0 || yandexMetricaInitialized) return
  yandexMetricaInitialized = true
  ensureYandexMetrica()(yandexMetricaId, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: false,
  })
}

function injectYandexMetrica() {
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://mc.yandex.ru/metrika/tag.js'
  document.head.appendChild(script)
}

function injectAnalytics() {
  if (analyticsInjected) return
  analyticsInjected = true
  activationEvents.forEach((event) => window.removeEventListener(event, activateAnalytics))
  injectGtm()
  if (yandexMetricaInitialized) injectYandexMetrica()
}

function activateAnalytics() {
  injectAnalytics()
}

function scheduleAnalyticsOnIntent() {
  activationEvents.forEach((event) => window.addEventListener(event, activateAnalytics, {
    once: true,
    passive: true,
  }))
}

export function initializeAnalytics() {
  window.dataLayer = window.dataLayer || []

  if (
    !gtmId || !/^GTM-[A-Z0-9]+$/.test(gtmId)
    || !googleAdsId || !/^AW-[0-9]+$/.test(googleAdsId)
    || analyticsScheduled
  ) return
  analyticsScheduled = true

  window.dataLayer.push({ event: 'gtm.js', 'gtm.start': Date.now() })
  gtag('js', new Date())
  gtag('config', googleAdsId, { allow_ad_personalization_signals: false })
  initializeYandexMetrica()
  scheduleAnalyticsOnIntent()
}

export function trackEvent(event: string, parameters: AnalyticsParameters = {}) {
  activateAnalytics()
  window.dataLayer = window.dataLayer || []
  const googleParameters = Object.fromEntries(Object.entries(parameters).map(([key, value]) => [
    key,
    Array.isArray(value) ? value.join(',') : value,
  ]))
  gtag('event', event, { ...googleParameters, send_to: ga4MeasurementId })
  if (yandexGoalEvents.has(event) && yandexMetricaInitialized) {
    window.ym?.(yandexMetricaId, 'reachGoal', event)
  }
}

export function trackEventOnce(event: string, parameters: AnalyticsParameters = {}) {
  if (onceOnlyEvents.has(event)) return
  onceOnlyEvents.add(event)
  trackEvent(event, parameters)
}

export function trackPageView(url = window.location.href) {
  if (!yandexMetricaInitialized || url === lastTrackedUrl) return
  activateAnalytics()
  window.ym?.(yandexMetricaId, 'hit', url, {
    title: document.title,
    referer: lastTrackedUrl,
  })
  lastTrackedUrl = url
}

export function trackWaitlistConversion() {
  gtag('event', 'conversion', {
    send_to: waitlistConversionDestination,
    value: 1,
    currency: 'USD',
  })
}
